import axios, { getData, isAxiosError } from "../../../axios";
import {
  createSlice,
  createAction,
  isAnyOf,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
} from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import * as types from "./types";
import * as selectors from "./selectors";
import * as notificationActions from "../../notifications";
import { createErrorReportingAsyncThunk, unwrap } from "../../helpers";
import { prettyPrintIfDecodeError } from "../../../helpers/io-ts-helpers";
import type { AppDispatch, RootState } from "../../../redux-store";
import {
  CreateUpdateExpense,
  createUpdateExpenseT,
  CreateUpdateInvoiceLine,
  createUpdateInvoiceLineT,
  sendInvoiceRequestT,
  updateSurchargeLineT,
  updateInvoiceLineOrderNumberT,
  SendInvoiceType,
} from "./types";
import { LineField, remoteLineChanges } from "./remote-line-changes";

const prefix = "data/deliveries";

export interface UpdateSurchargeLine {
  id: string;
  percentage: string;
}

export type Id = {
  id: string;
};

export interface DeliveryId {
  deliveryId: string;
}

export const sendExpenses = createErrorReportingAsyncThunk(
  `${prefix}/load-expenses`,
  async (input: { deliveryId: string; expenseIds: string[] }, thunkAPI) => {
    const { deliveryId, expenseIds } = input;
    const delivery = await axios
      .post(`/api/deliveries/${deliveryId}/expense-batches`, {
        expenseIds,
      })
      .then(getData)
      .then(tPromise.decode(types.deliveryT));
    thunkAPI.dispatch(deliveryUpdated(delivery));
  }
);

export const deleteExpense = createErrorReportingAsyncThunk(
  `${prefix}/delete-expense`,
  async (input: { deliveryId: string; expenseId: string }, thunkAPI) => {
    const { deliveryId, expenseId } = input;
    const delivery = await axios
      .delete(`/api/deliveries/${deliveryId}/expenses/${expenseId}`)
      .then(getData)
      .then(tPromise.decode(types.deliveryT));
    thunkAPI.dispatch(deliveryUpdated(delivery));
  }
);

export const deliveryRevisionNudged = createAction<{
  deliveryId: string;
  revision: string | null;
}>(`${prefix}/revisionNudged`);

export const linesChangedRemotely = createAction<{
  deliveryId: string;
  changed: Record<string, LineField[]>;
  deleted: string[];
}>(`${prefix}/linesChangedRemotely`);

export const deliveryUpdated = createAction<types.Delivery>(
  `${prefix}/updated`
);

export const addExpense = createErrorReportingAsyncThunk(
  `${prefix}/addExpense`,
  async (input: CreateUpdateExpense & DeliveryId, thunkAPI) => {
    const { deliveryId, accountId, amount, currency, date } = input;
    const data = {
      accountId,
      amount,
      currency,
      date,
    };
    const delivery = await axios
      .post(
        `/api/deliveries/${deliveryId}/expenses`,
        createUpdateExpenseT.encode(data)
      )
      .then(getData)
      .then(tPromise.decode(types.deliveryT));
    thunkAPI.dispatch(deliveryUpdated(delivery));
  }
);

export const updateExpense = createErrorReportingAsyncThunk(
  `${prefix}/updateExpense`,
  async (
    input: CreateUpdateExpense & { deliveryId: string; expenseId: string },
    thunkAPI
  ) => {
    const { expenseId, deliveryId, accountId, amount, currency, date } = input;
    const data = {
      accountId,
      amount,
      currency,
      date,
    };
    const delivery = await axios
      .put(
        `/api/deliveries/${deliveryId}/expenses/${expenseId}`,
        createUpdateExpenseT.encode(data)
      )
      .then(getData)
      .then(tPromise.decode(types.deliveryT));
    thunkAPI.dispatch(deliveryUpdated(delivery));
  }
);

const fetchDelivery = (deliveryId: string) =>
  axios
    .get(`/api/deliveries/${deliveryId}`)
    .then((x) => x.data)
    .then(tPromise.decode(types.deliveryT))
    .catch(prettyPrintIfDecodeError);

export const loadDelivery = createErrorReportingAsyncThunk(
  `${prefix}/getDelivery`,
  async (deliveryId: string, thunkAPI) => {
    const delivery = await fetchDelivery(deliveryId);
    thunkAPI.dispatch(deliveryUpdated(delivery));
  }
);

// Someone else changed the delivery: refetch it and record how its lines
// changed, for the invoice lines table. The user didn't ask for this, so a
// failure is reported but not shown; the next nudge tries again.
export const refetchChangedDelivery = createErrorReportingAsyncThunk(
  `${prefix}/refetchChangedDelivery`,
  async (deliveryId: string, thunkAPI) => {
    const before = selectors.selectInvoiceLines(deliveryId)(
      thunkAPI.getState()
    );
    const delivery = await fetchDelivery(deliveryId);
    thunkAPI.dispatch(deliveryUpdated(delivery));
    thunkAPI.dispatch(
      linesChangedRemotely({
        deliveryId,
        ...remoteLineChanges(before, delivery.lines),
      })
    );
  },
  { skipNotification: true }
);

export const addLine = createErrorReportingAsyncThunk(
  `${prefix}/addLine`,
  async (line: CreateUpdateInvoiceLine & DeliveryId, thunkAPI) => {
    const { deliveryId, ...data } = line;
    const { delivery, newLinesIds } = await axios
      .post(
        `/api/deliveries/${deliveryId}/invoice-lines`,
        createUpdateInvoiceLineT.encode(data)
      )
      .then(getData)
      .then(tPromise.decode(types.deliveryUpdateResponseT));
    thunkAPI.dispatch(deliveryUpdated(delivery));
    return newLinesIds;
  }
);

export const deleteLine = createErrorReportingAsyncThunk(
  `${prefix}/delete`,
  async (input: { deliveryId: string; lineId: string }, thunkAPI) => {
    const { deliveryId, lineId } = input;
    const delivery = await axios
      .delete(`/api/deliveries/${deliveryId}/invoice-lines/${lineId}`)
      .then(getData)
      .then(tPromise.decode(types.deliveryT));
    thunkAPI.dispatch(deliveryUpdated(delivery));
  }
);

// A 409 on a line edit means the delivery changed under the user, most often
// because the line was invoiced after the edit started (from this dialog or
// another tab). The server is right to refuse, so this is not a bug to report:
// reload the delivery, which locks the line and closes its edit row, and tell
// the user why their change didn't go through.
const handleLineConflict = async (
  deliveryId: string,
  lineId: string,
  thunkAPI: { dispatch: AppDispatch; getState: () => RootState }
) => {
  await thunkAPI.dispatch(loadDelivery(deliveryId));
  const line = selectors
    .selectInvoiceLines(deliveryId)(thunkAPI.getState())
    .find((l) => l.id === lineId);
  thunkAPI.dispatch(
    notificationActions.notifyL({
      namespace: "notifications",
      key: line?.locked ? "invoiceLineAlreadyInvoiced" : "updatingFailed",
      type: "error",
    })
  );
};

export const updateLine = createErrorReportingAsyncThunk(
  `${prefix}/updateLine`,
  async (line: CreateUpdateInvoiceLine & Id & DeliveryId, thunkAPI) => {
    try {
      const { deliveryId, id, ...data } = line;
      const { delivery, newLinesIds } = await axios
        .put(`/api/deliveries/${deliveryId}/invoice-lines/${id}`, data)
        .then(getData)
        .then(tPromise.decode(types.deliveryUpdateResponseT));
      thunkAPI.dispatch(deliveryUpdated(delivery));
      thunkAPI.dispatch(
        notificationActions.notifyL({
          namespace: "notifications",
          key: "invoiceLineUpdated",
        })
      );
      return newLinesIds;
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 409) {
        await handleLineConflict(line.deliveryId, line.id, thunkAPI);
        return thunkAPI.rejectWithValue("CONFLICT");
      }
      throw e;
    }
  }
);

export const updateLineOrderNumber = createErrorReportingAsyncThunk(
  `${prefix}/updateLineOrderNumber`,
  async (input: {
    deliveryId: string;
    lineId: string;
    orderNumber: number;
  }) => {
    const { deliveryId, lineId, orderNumber } = input;
    return axios
      .put(
        `/api/deliveries/${deliveryId}/invoice-lines/${lineId}/set-order-number`,
        updateInvoiceLineOrderNumberT.encode({ orderNumber })
      )
      .then(getData)
      .then(tPromise.decode(types.deliveryT));
  }
);

const doUpdateSurchargeLine = createErrorReportingAsyncThunk(
  "data/deliveries/updateSurchargeLine",
  async (line: UpdateSurchargeLine & DeliveryId, thunkAPI) => {
    try {
      const { deliveryId, id, ...data } = line;
      const delivery = await axios
        .put(
          `/api/deliveries/${deliveryId}/invoice-lines/${id}/set-percentage`,
          updateSurchargeLineT.encode(data)
        )
        .then(getData)
        .then(tPromise.decode(types.deliveryT));
      thunkAPI.dispatch(deliveryUpdated(delivery));
      thunkAPI.dispatch(
        notificationActions.notifyL({
          namespace: "notifications",
          key: "invoiceLineUpdated",
        })
      );
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 409) {
        await handleLineConflict(line.deliveryId, line.id, thunkAPI);
        return thunkAPI.rejectWithValue("CONFLICT");
      }
      throw e;
    }
  }
);

export const updateSurchargeLine = unwrap(doUpdateSurchargeLine);

export const sendInvoice = createErrorReportingAsyncThunk(
  "data/deliveries/sendInvoice",
  async (
    input: {
      deliveryId: string;
      selectedLineIds: string[];
      clientId?: string;
      customerContactId?: string | null;
      departmentId?: string | null;
      attachmentFileIds?: string[];
      folderName?: string;
      deliveryAddressId?: string | null;
      dueDate: string | null;
      type: SendInvoiceType;
    },
    thunkAPI
  ) => {
    const {
      deliveryId,
      clientId,
      customerContactId,
      departmentId,
      selectedLineIds,
      attachmentFileIds,
      type,
      dueDate,
      folderName,
      deliveryAddressId,
    } = input;
    const revision = selectors.selectRevision(deliveryId)(thunkAPI.getState());
    try {
      const delivery = await axios
        .post(
          `/api/deliveries/${deliveryId}/invoices`,
          sendInvoiceRequestT.encode({
            revision,
            clientId,
            customerContactId,
            departmentId,
            lineIds: selectedLineIds,
            attachmentFileIds,
            folderName,
            deliveryAddressId,
            dueDate,
            type,
          })
        )
        .then((x) => x.data)
        .then(tPromise.decode(types.deliveryT));
      thunkAPI.dispatch(deliveryUpdated(delivery));
    } catch (e: any) {
      if (isAxiosError(e) && e.response?.status === 409) {
        thunkAPI.dispatch(
          notificationActions.notifyL({
            namespace: "notifications",
            key: "invoiceFailedDueToEditedLines",
            type: "error",
          })
        );
        const delivery = await tPromise.decode(
          types.deliveryT,
          e.response?.data
        );
        thunkAPI.dispatch(deliveryUpdated(delivery));
        // Nothing was invoiced. Reject so a caller awaiting the send does not
        // treat the lines as invoiced; the delivery now has the fresh revision,
        // so sending again can succeed.
        return thunkAPI.rejectWithValue("CONFLICT");
      }
      throw e;
    }
  }
);

interface DeliveriesState {
  entities: {
    [key: string]: types.Delivery;
  };
  // How the lines changed the last time someone else changed the delivery.
  // `seq` counts those refetches, so the same change twice is seen twice.
  remoteLineChanges: {
    [deliveryId: string]: {
      seq: number;
      changed: Record<string, LineField[]>;
      deleted: string[];
    };
  };
  // The user's own changes to the delivery still on their way. A nudge that
  // arrives meanwhile waits for them: it may be for one of them, and only the
  // response says which revision that is.
  changesInFlight: { [deliveryId: string]: number };
}

const initialState: DeliveriesState = {
  entities: {},
  remoteLineChanges: {},
  changesInFlight: {},
};

const ownChanges = [
  addLine,
  updateLine,
  deleteLine,
  doUpdateSurchargeLine,
  updateLineOrderNumber,
  sendInvoice,
] as const;

const slice = createSlice({
  name: "data/deliveries",
  initialState,
  reducers: {
    unloadDelivery: (state, action: PayloadAction<string>) => {
      delete state.entities[action.payload];
      delete state.remoteLineChanges[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deliveryUpdated, (state, action) => {
      state.entities[action.payload.id] = action.payload;
    });
    builder.addCase(updateLineOrderNumber.fulfilled, (state, action) => {
      state.entities[action.payload.id] = action.payload;
    });
    builder.addCase(linesChangedRemotely, (state, action) => {
      const { deliveryId, changed, deleted } = action.payload;
      const seq = (state.remoteLineChanges[deliveryId]?.seq ?? 0) + 1;
      state.remoteLineChanges[deliveryId] = { seq, changed, deleted };
    });
    builder.addMatcher(isPending(...ownChanges), (state, action) => {
      const { deliveryId } = action.meta.arg;
      state.changesInFlight[deliveryId] =
        (state.changesInFlight[deliveryId] ?? 0) + 1;
    });
    builder.addMatcher(
      isAnyOf(isFulfilled(...ownChanges), isRejected(...ownChanges)),
      (state, action) => {
        const { deliveryId } = action.meta.arg;
        const left = (state.changesInFlight[deliveryId] ?? 1) - 1;
        if (left > 0) {
          state.changesInFlight[deliveryId] = left;
        } else {
          delete state.changesInFlight[deliveryId];
        }
      }
    );
  },
});

export default slice.reducer;
export const { unloadDelivery } = slice.actions;
